import * as React from 'react'
import type { OutputCheck } from '@axilon-apps/domains-ontology-ui-state'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { NativeSelect, NativeSelectOption } from '../../../components/ui/native-select'
import { Textarea } from '../../../components/ui/textarea'
import { changeReasonGiven } from './changeReason'
import {
  EDITOR_BODY_CLASS,
  EDITOR_CARD_CLASS,
  EDITOR_DIALOG_CLASS,
  EDITOR_INPUT_CLASS,
  EDITOR_SELECT_CLASS,
  EditorDialogHeader,
  EditorError,
  EditorField
} from './editorChrome'

const REASON_PREFILL = 'checks edited: '

export interface EditChecksSubmit {
  checks: OutputCheck[]
  /** Only in `revise` mode. */
  changeReason?: string
}

/**
 * Rows of table / message / SQL. In `revise` mode the submit saves a new
 * version directly (a reason is required); in `overrides` mode it edits the
 * draft in page state and *Save as vN* carries it.
 */
export function EditChecksDialog({
  open,
  onOpenChange,
  mode,
  tables,
  initial,
  loading,
  error,
  onSubmit
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'revise' | 'overrides'
  tables: string[]
  initial: OutputCheck[]
  loading: boolean
  error?: string
  onSubmit: (input: EditChecksSubmit) => void
}) {
  const [checks, setChecks] = React.useState<OutputCheck[]>(initial)
  const [reason, setReason] = React.useState(REASON_PREFILL)
  // Snapshot the starting point when the dialog opens — not whenever the
  // parent re-renders with a fresh `initial` identity mid-edit.
  const initialRef = React.useRef(initial)
  initialRef.current = initial
  React.useEffect(() => {
    if (open) {
      setChecks(initialRef.current)
      setReason(REASON_PREFILL)
    }
  }, [open])
  const update = (index: number, patch: Partial<OutputCheck>) =>
    setChecks((current) =>
      current.map((check, i) => (i === index ? { ...check, ...patch } : check))
    )
  const complete = checks.every((check) => check.table && check.sql.trim() && check.message.trim())
  const reasonOk = mode === 'overrides' || changeReasonGiven(reason, REASON_PREFILL)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${EDITOR_DIALOG_CLASS} sm:max-w-3xl`}>
        <EditorDialogHeader title={mode === 'revise' ? 'Edit checks' : 'Edit checks on this draft'}>
          Each check is one <code className="font-mono">SELECT</code> over the produced table,
          aliased <code className="font-mono">t</code>, returning one boolean (true = pass).{' '}
          {mode === 'revise'
            ? 'Saving creates a new version; no builder runs.'
            : 'The edit is saved with the draft when you press Save.'}
        </EditorDialogHeader>
        <div className={EDITOR_BODY_CLASS}>
          {checks.length === 0 ? (
            <p className="text-xs text-ink-3">No checks yet — every produced table is accepted.</p>
          ) : null}
          {checks.map((check, index) => (
            <div key={index} className={`${EDITOR_CARD_CLASS} flex flex-col gap-2`}>
              <div className="flex items-center gap-2">
                <span className="w-4 shrink-0 text-11 font-medium text-ink-3 tabular-nums">
                  {index + 1}
                </span>
                {tables.length > 1 ? (
                  <NativeSelect
                    size="sm"
                    className={`${EDITOR_SELECT_CLASS} max-w-[14rem] font-mono`}
                    aria-label={`Check ${index + 1} table`}
                    value={check.table}
                    onChange={(event) => update(index, { table: event.target.value })}
                  >
                    {tables.map((table) => (
                      <NativeSelectOption key={table} value={table}>
                        {table}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                ) : (
                  <span
                    className="max-w-[14rem] shrink-0 truncate rounded bg-bg-sunk px-1.5 py-0.5 font-mono text-11 text-ink-2"
                    title={check.table}
                  >
                    {check.table}
                  </span>
                )}
                <Input
                  aria-label={`Check ${index + 1} message`}
                  placeholder="Message shown when the check fails"
                  className={`${EDITOR_INPUT_CLASS} min-w-0 flex-1`}
                  value={check.message}
                  onChange={(event) => update(index, { message: event.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="shrink-0 text-ink-3 hover:text-st-down"
                  aria-label={`Remove check ${index + 1}`}
                  onClick={() => setChecks((current) => current.filter((_, i) => i !== index))}
                >
                  <Trash2 />
                </Button>
              </div>
              <Textarea
                aria-label={`Check ${index + 1} SQL`}
                className="min-h-0 px-2 py-1.5 font-mono text-11 leading-5 md:text-11"
                rows={2}
                spellCheck={false}
                placeholder="SELECT count(*) = 0 FROM t WHERE qty < 0"
                value={check.sql}
                onChange={(event) => update(index, { sql: event.target.value })}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="w-fit"
            onClick={() =>
              setChecks((current) => [...current, { table: tables[0] ?? '', sql: '', message: '' }])
            }
          >
            <Plus /> Add check
          </Button>
        </div>
        {mode === 'revise' ? (
          <EditorField
            label="Why this change (saved with the version)"
            ariaLabel="Change reason"
            value={reason}
            onChange={setReason}
          />
        ) : null}
        <EditorError error={error} />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={loading || !complete || !reasonOk}
            onClick={() =>
              onSubmit({
                checks: checks.map((check) => ({
                  table: check.table,
                  sql: check.sql.trim(),
                  message: check.message.trim()
                })),
                ...(mode === 'revise' ? { changeReason: reason.trim() } : {})
              })
            }
          >
            {mode === 'revise' ? 'Save as new version' : 'Apply to draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
